import { FC, KeyboardEvent, ReactNode } from "react";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailEditorDevice, useEmailEditorDevice } from "@/components/EmailEditorDevice/EmailEditorDeviceContext";
import Image from "@/components/Image/Image";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ArrowRight, Cross } from "@/icons/outlined";

export type EmailTypeChoice = "designed" | "simple";

interface EmailTypeChoiceDrawerProps {
  open: boolean;
  onClose: () => void;
  onChoose: (choice: EmailTypeChoice) => void;
  disabled?: boolean;
}

export const EmailTypeChoiceDrawer: FC<EmailTypeChoiceDrawerProps> = ({ open, onClose, onChoose, disabled }) => {
  const { t } = useTranslate();
  const device = useEmailEditorDevice();
  const title = t("dashboard", "common.newEmail");

  const mobileHeader = (
    <Stack>
      <Stack direction="row" alignItems="center" gap={1} sx={{ paddingY: 2, paddingLeft: 3, paddingRight: 2 }}>
        <DashboardTypography variant="h5" vibe="text-neutral-intense" sx={{ flex: 1 }}>
          {title}
        </DashboardTypography>
        <DashboardIconButton
          variant="text"
          vibe="neutral"
          size="medium"
          onClick={onClose}
          aria-label={t("dashboard", "common.close")}
          data-test="email-type-choice-close"
        >
          <Cross />
        </DashboardIconButton>
      </Stack>
      <Divider />
    </Stack>
  );

  return (
    <DashboardDrawer
      open={open}
      onClose={onClose}
      size="small"
      {...(device === EmailEditorDevice.Mobile ? { slots: { header: mobileHeader } } : { title })}
      data-test="email-type-choice-drawer"
      noPadding
    >
      <Stack
        spacing={{ xs: 2, md: 3 }}
        sx={{ paddingTop: 3, paddingX: { xs: 3, md: 2 }, paddingBottom: { xs: 3, md: 2 } }}
      >
        <DashboardTypography variant="subtitle2" vibe="text-neutral-quiet" sx={{ textTransform: "uppercase" }}>
          {t("dashboard", "emailTypeChoice.sectionTitle")}
        </DashboardTypography>
        <Stack spacing={{ xs: 2, md: 3 }}>
          <EmailTypeCard
            illustration={<Image alt="" src="/images/email-type-choice/designed-email.svg" width={80} height={110} />}
            title={t("dashboard", "emailTypeChoice.designed.title")}
            description={t("dashboard", "emailTypeChoice.designed.description")}
            disabled={disabled}
            dataTest="email-type-choice-designed"
            onChoose={() => onChoose("designed")}
          />
          <EmailTypeCard
            illustration={<Image alt="" src="/images/email-type-choice/simple-email.svg" width={80} height={110} />}
            title={t("dashboard", "emailTypeChoice.simple.title")}
            description={t("dashboard", "emailTypeChoice.simple.description")}
            disabled={disabled}
            dataTest="email-type-choice-simple"
            onChoose={() => onChoose("simple")}
          />
        </Stack>
      </Stack>
    </DashboardDrawer>
  );
};

interface EmailTypeCardProps {
  illustration: ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  dataTest: string;
  onChoose: () => void;
}

const EmailTypeCard: FC<EmailTypeCardProps> = ({ illustration, title, description, disabled, dataTest, onChoose }) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onChoose();
    }
  };

  return (
    <Box
      role="button"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? undefined : 0}
      onClick={disabled ? undefined : onChoose}
      onKeyDown={disabled ? undefined : handleKeyDown}
      data-test={dataTest}
      sx={(theme) => ({
        display: "flex",
        gap: 2,
        alignItems: { xs: "center", md: "flex-end" },
        width: "100%",
        paddingBlock: { xs: 2, md: 1.375 },
        paddingInline: 2,
        borderRadius: { xs: theme.radius(1.5), md: theme.spacing(1) },
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        "&:hover": disabled ? undefined : { borderColor: theme.palette.border.brand.moderate },
      })}
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          gap: { xs: 2, md: 1.5 },
          alignItems: { xs: "center", md: "flex-start" },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: "0 0 auto",
            "& img": { width: { xs: 71, md: 80 }, height: { xs: 105, md: 110 }, objectFit: "contain" },
          }}
        >
          {illustration}
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0, maxWidth: 248 }} gap={{ xs: 0.25, md: 0 }}>
          <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
            {title}
          </DashboardTypography>
          <DashboardTypography variant="body2" vibe="text-neutral-quiet">
            {description}
          </DashboardTypography>
        </Stack>
      </Box>
      <Box
        aria-hidden
        sx={(theme) => ({
          display: "flex",
          flex: "0 0 auto",
          alignItems: "center",
          justifyContent: "center",
          padding: { xs: 0, md: 0.625 },
          borderRadius: { xs: 0, md: theme.spacing(1) },
          border: { xs: "none", md: `1px solid ${theme.palette.border.neutral.moderate}` },
          color: { xs: theme.palette.text.neutral.intense, md: theme.palette.text.neutral.moderate },
          "& svg": { height: 20, width: 20 },
        })}
      >
        <ArrowRight />
      </Box>
    </Box>
  );
};
